/*!
 * k47-transit.js — Hero „Fig. 01 — Transit“ für 47*coffee
 * Custom Element <k47-transit>, keine Abhängigkeiten.
 *
 * Hinter dem Beutel kreisen die beiden Sonnen von Kepler-47 auf ihrer echten Bahn
 * (Periode 7,448 d, Massenverhältnis 0,957 : 0,342, Exzentrizität 0,0288).
 * Der Beutel steht davor wie ein Planet: Er verdeckt die Sonnen abwechselnd (Transit),
 * die Sonnen verdecken sich gegenseitig (Eklipse), das Gegenlicht liegt an seinen Kanten.
 *
 * Attribute
 *   src="…png|webp"        freigestellter Beutel mit transparentem Hintergrund (Pflicht)
 *   alt="…"                Bildbeschreibung, z. B. „Kaffeebeutel Zenit“
 *   speed="2"              Sekunden pro simuliertem Tag (2 → ein Umlauf in ca. 15 s)
 *   fig="Fig. 01 — Transit"    Beschriftung links oben
 *   code="47 — ZN · Spec K"    Beschriftung rechts oben
 *   note="Kepler-47 A · B"     Beschriftung links unten
 *   captions               Beschriftungen anzeigen
 *   lightcurve             Lichtkurve am unteren Rand (schematisch)
 *   paused                 startet angehalten (bei prefers-reduced-motion automatisch)
 *
 * Gedacht für dunkle Flächen (Orbit). Größe: füllt den Host; ohne feste Höhe gilt
 * aspect-ratio 665 / 576 (anpassbar per CSS auf dem Element).
 */
(() => {
  if (typeof window === 'undefined' || !window.customElements || customElements.get('k47-transit')) return;

  const TAU = Math.PI * 2;
  const RAD = Math.PI / 180;
  const MA = 0.957;
  const MB = 0.342;
  const M = MA + MB;
  const BIN = { P: 7.4483648, e: 0.0288, w: 226.3 * RAD, phase0: 0 };
  const TILT = 0.2;          // leichter Blick von oben auf die fast seitlich gesehene Bahn
  const WEIGHT_A = 0.85;     // Gewichtung in der (schematischen) Lichtkurve
  const WEIGHT_B = 0.15;
  const CURVE_DAYS = 2 * BIN.P;

  const SUN = {
    A: { core: ['255,255,255', '255,236,204'], glow: '255,196,120', label: 'A' },
    B: { core: ['255,226,206', '255,122,70'], glow: '255,104,56', label: 'B' }
  };

  function kepler(e, w, Mn) {
    let E = Mn + e * Math.sin(Mn);
    for (let i = 0; i < 4; i++) E -= (E - e * Math.sin(E) - Mn) / (1 - e * Math.cos(E));
    const x = Math.cos(E) - e;
    const y = Math.sqrt(1 - e * e) * Math.sin(E);
    return { X: x * Math.cos(w) - y * Math.sin(w), Y: x * Math.sin(w) + y * Math.cos(w) };
  }
  // Positionen in Einheiten der großen Halbachse der Relativbahn
  function stars(t) {
    const r = kepler(BIN.e, BIN.w, TAU * (t / BIN.P + BIN.phase0));
    return { A: { X: -(MB / M) * r.X, Y: -(MB / M) * r.Y }, B: { X: (MA / M) * r.X, Y: (MA / M) * r.Y } };
  }

  const CSS = `
:host{display:block;position:relative;container-type:inline-size;aspect-ratio:665/576;min-height:260px;overflow:hidden;color:var(--ink,#F2EFE8);font-family:var(--font-mono,ui-monospace,Menlo,monospace)}
*{box-sizing:border-box}
[hidden]{display:none!important}
canvas{position:absolute;left:0;top:0;width:100%;height:100%;display:block}
.cap{position:absolute;font-size:11px;line-height:16px;letter-spacing:.55px;text-transform:uppercase;color:var(--ink-secondary,#A6A39C);white-space:nowrap;pointer-events:none}
.tl{top:14px;left:16px}.tr{top:14px;right:16px}
.bl{left:16px;bottom:18px;display:flex;align-items:center;gap:8px}
.dot{display:inline-block;border-radius:50%}
.curve{position:absolute;left:16px;right:16px;bottom:46px;height:44px;pointer-events:none}
.curve canvas{position:static;height:44px}
.curve span{position:absolute;left:0;top:-14px;font-size:10px;line-height:14px;letter-spacing:.5px;text-transform:uppercase;color:var(--ink-secondary,#A6A39C)}
.toggle{position:absolute;right:8px;bottom:6px;min-height:44px;min-width:44px;padding:0 14px;border:1px solid var(--line,#2B2B2B);border-radius:0;background:transparent;color:var(--ink,#F2EFE8);font:500 11px/16px var(--font-mono,ui-monospace,monospace);letter-spacing:.55px;text-transform:uppercase;cursor:pointer}
.toggle:hover{border-color:var(--ink,#F2EFE8)}
.toggle:focus-visible{outline:2px solid var(--focus,var(--accent,#FFB35C));outline-offset:2px}
@container (max-width: 420px){ .tr{display:none} }
`;

  class K47Transit extends HTMLElement {
    static get observedAttributes() { return ['src', 'alt', 'speed', 'fig', 'code', 'note', 'captions', 'lightcurve', 'paused']; }

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.t = 0;
      this.running = false;
      this.visible = true;
      this.curve = [];
      this._frame = this._frame.bind(this);
    }

    get speed() { const v = parseFloat(this.getAttribute('speed')); return v > 0 ? v : 2; }

    connectedCallback() {
      if (!this._built) this._build();
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = this.hasAttribute('paused') || reduce;
      this.t = this._restingTime();
      this._loadImage();
      this._ro = new ResizeObserver(() => { this._layout(); this._draw(); });
      this._ro.observe(this);
      this._io = new IntersectionObserver((es) => { this.visible = es.some((e) => e.isIntersecting); this._sync(); });
      this._io.observe(this);
      this._onVis = () => this._sync();
      document.addEventListener('visibilitychange', this._onVis);
      this._layout();
      this._prefill();
      this._draw();
      this._sync();
    }

    disconnectedCallback() {
      this._stop();
      if (this._ro) this._ro.disconnect();
      if (this._io) this._io.disconnect();
      document.removeEventListener('visibilitychange', this._onVis);
    }

    attributeChangedCallback(name) {
      if (!this._built) return;
      if (name === 'src') this._loadImage();
      if (name === 'paused') this.userPaused = this.hasAttribute('paused');
      this._applyText();
      this._layout();
      this._prefill();
      this._draw();
      this._sync();
    }

    pause() { this.userPaused = true; this._sync(); }
    play() { this.userPaused = false; this._sync(); }

    _build() {
      this._built = true;
      this.shadowRoot.innerHTML = `<style>${CSS}</style>
<canvas class="scene" role="img"></canvas>
<div class="cap tl"></div>
<div class="cap tr"></div>
<div class="curve"><span>Lichtkurve · schematisch</span><canvas class="lc" aria-hidden="true"></canvas></div>
<div class="cap bl"><span class="dot" style="width:8px;height:8px;background:rgb(255,214,160)"></span><span class="dot" style="width:5px;height:5px;background:rgb(255,122,70)"></span><span class="nt"></span></div>
<button class="toggle" type="button"></button>`;
      const q = (s) => this.shadowRoot.querySelector(s);
      this.$scene = q('.scene');
      this.$tl = q('.tl');
      this.$tr = q('.tr');
      this.$bl = q('.bl');
      this.$nt = q('.nt');
      this.$curveBox = q('.curve');
      this.$lc = q('.lc');
      this.$toggle = q('.toggle');
      this.$toggle.addEventListener('click', () => { this.userPaused = !this.userPaused; this._sync(); });
      this._applyText();
    }

    _applyText() {
      const caps = this.hasAttribute('captions');
      this.$tl.textContent = this.getAttribute('fig') || 'Fig. 01 — Transit';
      this.$tr.textContent = this.getAttribute('code') || '47 — ZN · Spec K';
      this.$nt.textContent = this.getAttribute('note') || 'Kepler-47 A · B';
      [this.$tl, this.$tr, this.$bl].forEach((el) => { el.hidden = !caps; });
      this.$curveBox.hidden = !this.hasAttribute('lightcurve');
      const alt = this.getAttribute('alt') || 'Kaffeebeutel';
      this.$scene.setAttribute('aria-label', `${alt} vor den zwei Sonnen von Kepler-47, die hinter dem Beutel umeinander kreisen.`);
    }

    _loadImage() {
      const src = this.getAttribute('src');
      if (!src) return;
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => { this.img = img; this._layout(); this._prefill(); this._draw(); };
      img.src = src;
    }

    // Ruhebild: B rechts außen und leicht hinten, A halb über dem Beutelrand
    _restingTime() {
      let best = 0;
      let score = -1e9;
      for (let i = 0; i < 400; i++) {
        const t = (i / 400) * BIN.P;
        const s = stars(t);
        const v = s.B.X + 0.6 * s.B.Y;
        if (v > score) { score = v; best = t; }
      }
      return best;
    }

    _layout() {
      const W = this.clientWidth || 600;
      const H = this.clientHeight || 520;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.$scene.width = Math.round(W * dpr);
      this.$scene.height = Math.round(H * dpr);
      const ratio = this.img ? this.img.naturalWidth / this.img.naturalHeight : 0.751;
      const rA = Math.max(9, Math.min(W, H) * 0.055);
      const rB = rA * 0.5;
      // oben: Beschriftung + höchster Punkt der Sonnenbahn; unten: Pause-Knopf bzw. Lichtkurve
      const top = (this.hasAttribute('captions') ? 40 : 16) + Math.max(W * 0.42 * TILT + rB, rA * 1.2);
      // Reserve unten per CSS steuerbar (--transit-bottom), z. B. für den Leitsorten-CTA
      const reserve = parseFloat(getComputedStyle(this).getPropertyValue('--transit-bottom')) || (this.hasAttribute('lightcurve') ? 120 : 58);
      const bottom = H - reserve;
      // Beutel auf ganze Gerätepixel legen, damit die Kante nicht nachgerechnet wird
      const snap = (v) => Math.round(v * dpr) / dpr;
      const bh = snap(Math.max(40, Math.min(H * 0.7, (W * 0.6) / ratio, bottom - top)));
      const bw = snap(bh * ratio);
      const bx = snap((W - bw) / 2);
      const by = snap(top + Math.max(0, bottom - top - bh) * 0.55);
      const orbitR = Math.min(W * 0.42, bw * 0.95);
      this.g = {
        W, H, dpr, bw, bh, bx, by,
        cx: W / 2,
        cy: by + bh * 0.02,
        scale: orbitR / ((MA / M) * (1 + BIN.e)),
        rA,
        rB,
        glowA: Math.max(W, H) * 0.55,
        glowB: Math.max(W, H) * 0.28
      };
      if (this.img) {
        // skalierter Beutel, Maske für die Verdeckung, Arbeitsflächen für das Gegenlicht
        const dw = Math.max(1, Math.round(bw * dpr));
        const dh = Math.max(1, Math.round(bh * dpr));
        this.bag = this._scaled(this.img, dw, dh);
        this.lit = this._canvas(dw, dh);
        this.rim = this._canvas(dw, dh);
        // Innenkante der Silhouette: Fläche minus weichgezeichnete Fläche
        // (Weichzeichnung über den Schatten, damit es auch ohne ctx.filter läuft)
        const e1 = this._canvas(dw, dh);
        const ec = e1.getContext('2d');
        ec.drawImage(this.bag, 0, 0);
        ec.globalCompositeOperation = 'source-in';
        ec.fillStyle = '#fff';
        ec.fillRect(0, 0, dw, dh);
        ec.globalCompositeOperation = 'destination-out';
        ec.shadowColor = '#000';
        ec.shadowBlur = Math.max(4, bw * 0.035) * dpr;
        ec.shadowOffsetX = dw + 50;
        ec.drawImage(this.bag, -(dw + 50), 0);
        this.edge = this._canvas(dw, dh);
        const e2 = this.edge.getContext('2d');
        e2.drawImage(e1, 0, 0);
        e2.globalCompositeOperation = 'lighter';
        e2.drawImage(e1, 0, 0);
        const mw = Math.max(1, Math.round(bw));
        const mh = Math.max(1, Math.round(bh));
        const m = this._canvas(mw, mh);
        const mc = m.getContext('2d', { willReadFrequently: true });
        mc.drawImage(this.bag, 0, 0, mw, mh);
        this.mask = { w: mw, h: mh, data: mc.getImageData(0, 0, mw, mh).data };
      }
      if (this.hasAttribute('lightcurve')) {
        const cw = this.$lc.clientWidth || W - 32;
        this.$lc.width = Math.round(cw * dpr);
        this.$lc.height = Math.round(44 * dpr);
        this.lcg = { w: cw, h: 44, dpr };
      }
    }

    _canvas(w, h) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      return c;
    }

    // Verkleinern in Halbschritten: Canvas glättet beim direkten starken Verkleinern nicht
    // in allen Browsern, die Kante würde sonst treppig und wirkt ausgefranst
    _scaled(img, w, h) {
      let src = img;
      let sw = img.naturalWidth || img.width;
      let sh = img.naturalHeight || img.height;
      while (sw >= w * 2 && sh >= h * 2) {
        const c = this._canvas(Math.round(sw / 2), Math.round(sh / 2));
        const cc = c.getContext('2d');
        cc.imageSmoothingQuality = 'high';
        cc.drawImage(src, 0, 0, c.width, c.height);
        src = c;
        sw = c.width;
        sh = c.height;
      }
      const out = this._canvas(w, h);
      const oc = out.getContext('2d');
      oc.imageSmoothingQuality = 'high';
      oc.drawImage(src, 0, 0, w, h);
      return out;
    }

    _screen(t) {
      const g = this.g;
      const s = stars(t);
      const p = (o) => ({ x: g.cx + o.X * g.scale, y: g.cy - o.Y * g.scale * TILT, depth: o.Y });
      return { A: p(s.A), B: p(s.B) };
    }

    _behindBag(x, y) {
      const g = this.g;
      if (!this.mask) return false;
      const mx = Math.floor(x - g.bx);
      const my = Math.floor(y - g.by);
      if (mx < 0 || my < 0 || mx >= this.mask.w || my >= this.mask.h) return false;
      return this.mask.data[(my * this.mask.w + mx) * 4 + 3] > 127;
    }

    // sichtbarer Anteil einer Sonnenscheibe (Beutel davor, ggf. die andere Sonne davor)
    _visible(sun, r, other, rOther) {
      const N = 24;
      let vis = 0;
      let total = 0;
      for (let ring = 0; ring <= 2; ring++) {
        const rr = (ring / 2) * r * 0.95;
        const n = ring === 0 ? 1 : N / (3 - ring);
        for (let i = 0; i < n; i++) {
          const a = (i / n) * TAU;
          const x = sun.x + Math.cos(a) * rr;
          const y = sun.y + Math.sin(a) * rr;
          total += 1;
          if (this._behindBag(x, y)) continue;
          if (other && other.depth < sun.depth && Math.hypot(x - other.x, y - other.y) < rOther) continue;
          vis += 1;
        }
      }
      return vis / total;
    }

    _state(t) {
      const g = this.g;
      const s = this._screen(t);
      const visA = this._visible(s.A, g.rA, s.B, g.rB);
      const visB = this._visible(s.B, g.rB, s.A, g.rA);
      return { s, visA, visB, flux: WEIGHT_A * visA + WEIGHT_B * visB };
    }

    _prefill() {
      if (!this.g) return;
      this.curve = [];
      const steps = 220;
      for (let i = steps; i >= 1; i--) {
        const t = this.t - (i / steps) * CURVE_DAYS;
        this.curve.push({ t, f: this._state(t).flux });
      }
    }

    _sync() {
      if (!this.$toggle) return;
      const should = !this.userPaused && this.visible && document.visibilityState !== 'hidden';
      this.$toggle.textContent = this.userPaused ? 'Weiter' : 'Pause';
      this.$toggle.setAttribute('aria-label', this.userPaused ? 'Animation fortsetzen' : 'Animation anhalten');
      if (should && !this.running) {
        this.running = true;
        this._last = performance.now();
        this._raf = requestAnimationFrame(this._frame);
      } else if (!should) {
        this._stop();
      }
    }

    _stop() {
      this.running = false;
      if (this._raf) cancelAnimationFrame(this._raf);
      this._raf = null;
    }

    _frame(now) {
      if (!this.running) return;
      const dt = Math.min(0.1, (now - this._last) / 1000);
      this._last = now;
      this.t += dt / this.speed;
      this._draw();
      this._raf = requestAnimationFrame(this._frame);
    }

    _glow(ctx, p, radius, rgb, strength) {
      const gr = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
      gr.addColorStop(0, `rgba(${rgb},${0.55 * strength})`);
      gr.addColorStop(0.08, `rgba(${rgb},${0.32 * strength})`);
      gr.addColorStop(0.3, `rgba(${rgb},${0.1 * strength})`);
      gr.addColorStop(0.65, `rgba(${rgb},${0.025 * strength})`);
      gr.addColorStop(1, `rgba(${rgb},0)`);
      ctx.fillStyle = gr;
      ctx.beginPath(); ctx.arc(p.x, p.y, radius, 0, TAU); ctx.fill();
    }

    _core(ctx, p, r, cols) {
      const gr = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
      gr.addColorStop(0, `rgb(${cols[0]})`);
      gr.addColorStop(0.7, `rgb(${cols[1]})`);
      gr.addColorStop(1, `rgba(${cols[1]},0.85)`);
      ctx.fillStyle = gr;
      ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, TAU); ctx.fill();
    }

    _draw() {
      if (!this.g) return;
      const g = this.g;
      const ctx = this.$scene.getContext('2d');
      ctx.setTransform(g.dpr, 0, 0, g.dpr, 0, 0);
      ctx.clearRect(0, 0, g.W, g.H);
      const st = this._state(this.t);
      const { A, B } = st.s;

      // Licht der Sonnen (additiv)
      ctx.globalCompositeOperation = 'lighter';
      this._glow(ctx, A, g.glowA, SUN.A.glow, 0.35 + 0.65 * Math.max(st.visA, 0.25));
      this._glow(ctx, B, g.glowB, SUN.B.glow, 0.3 + 0.7 * Math.max(st.visB, 0.2));
      ctx.globalCompositeOperation = 'source-over';

      const hiddenA = 1 - st.visA;
      const hiddenB = 1 - st.visB;

      // weicher Lichtsaum um die Silhouette: nur der Schatten landet auf der Fläche,
      // der Beutel selbst wird außerhalb gezeichnet. Liegt unter den Sonnenscheiben,
      // damit eine halb verdeckte Sonne über der Kante klar bleibt.
      if (this.bag) {
        const rim = Math.min(1, 0.3 + 0.63 * hiddenA + 0.35 * hiddenB);
        const off = g.W + g.bw + 100;
        ctx.save();
        ctx.shadowColor = `rgba(255,214,160,${0.5 * rim})`;
        ctx.shadowBlur = (10 + 26 * rim) * g.dpr;
        ctx.shadowOffsetX = (off + ((A.x - g.cx) / g.W) * 10) * g.dpr;
        ctx.shadowOffsetY = -3 * g.dpr;
        ctx.drawImage(this.bag, g.bx - off, g.by, g.bw, g.bh);
        ctx.restore();
      }

      // Sonnenscheiben in Tiefenreihenfolge (die nähere verdeckt die fernere)
      const order = A.depth > B.depth ? [['A', A, g.rA], ['B', B, g.rB]] : [['B', B, g.rB], ['A', A, g.rA]];
      order.forEach(([k, p, r]) => this._core(ctx, p, r, SUN[k].core));

      // Beutel im Gegenlicht: Vorderseite leicht abgedunkelt, warme Lichtkante an der Silhouette
      if (this.bag) {
        const dw = this.lit.width;
        const dh = this.lit.height;
        const k = dw / g.bw;
        const rc = this.rim.getContext('2d');
        rc.globalCompositeOperation = 'source-over';
        rc.clearRect(0, 0, dw, dh);
        rc.globalCompositeOperation = 'lighter';
        [[A, '255,206,150', 0.55 + 0.4 * hiddenA, g.bh * 0.9], [B, '255,140,90', 0.3 + 0.45 * hiddenB, g.bh * 0.6]].forEach(([p, rgb, a, rad]) => {
          const px = (p.x - g.bx) * k;
          const py = (p.y - g.by) * k;
          const gr = rc.createRadialGradient(px, py, 0, px, py, rad * k);
          gr.addColorStop(0, `rgba(${rgb},${a})`);
          gr.addColorStop(0.45, `rgba(${rgb},${a * 0.5})`);
          gr.addColorStop(1, `rgba(${rgb},0)`);
          rc.fillStyle = gr;
          rc.fillRect(0, 0, dw, dh);
        });
        rc.globalCompositeOperation = 'destination-in';
        rc.drawImage(this.edge, 0, 0);

        const lc = this.lit.getContext('2d');
        lc.globalCompositeOperation = 'source-over';
        lc.clearRect(0, 0, dw, dh);
        lc.drawImage(this.bag, 0, 0);
        lc.globalCompositeOperation = 'source-atop';
        lc.fillStyle = 'rgba(10,8,6,0.18)';
        lc.fillRect(0, 0, dw, dh);
        lc.drawImage(this.rim, 0, 0);
        lc.globalCompositeOperation = 'source-over';
        ctx.drawImage(this.lit, g.bx, g.by, g.bw, g.bh);
      }

      // Lichtkurve
      if (this.hasAttribute('lightcurve') && this.lcg) {
        const last = this.curve.length ? this.curve[this.curve.length - 1].t : -1e9;
        if (this.t - last > CURVE_DAYS / 220) this.curve.push({ t: this.t, f: st.flux });
        while (this.curve.length && this.curve[0].t < this.t - CURVE_DAYS) this.curve.shift();
        this._drawCurve();
      }
    }

    _drawCurve() {
      const { w, h, dpr } = this.lcg;
      const ctx = this.$lc.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const cs = getComputedStyle(this);
      const ink = (cs.getPropertyValue('--ink') || '').trim() || '#F2EFE8';
      const line = (cs.getPropertyValue('--line') || '').trim() || '#2B2B2B';
      const t1 = this.t;
      const t0 = t1 - CURVE_DAYS;
      const x = (t) => ((t - t0) / CURVE_DAYS) * (w - 6);
      const y = (f) => 4 + (1 - f) * (h - 10);
      ctx.strokeStyle = line;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, y(1) + 0.5); ctx.lineTo(w, y(1) + 0.5); ctx.stroke();
      ctx.beginPath();
      this.curve.forEach((p, i) => { if (i === 0) ctx.moveTo(x(p.t), y(p.f)); else ctx.lineTo(x(p.t), y(p.f)); });
      ctx.strokeStyle = ink;
      ctx.lineWidth = 1.25;
      ctx.lineJoin = 'round';
      ctx.stroke();
      const lp = this.curve[this.curve.length - 1];
      if (lp) {
        ctx.fillStyle = 'rgb(255,179,92)';
        ctx.beginPath(); ctx.arc(x(lp.t), y(lp.f), 2.5, 0, TAU); ctx.fill();
      }
    }
  }

  customElements.define('k47-transit', K47Transit);
})();
